/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import { downloadCsvFile, generateFileName } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      reply_time: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      reply_time: [],
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
  },
  contactsReport: {
    isFetching: false,
    data: [],
  },
  contactsSummary: {
    contacts_created: { value: 0, previous: 0 },
  },
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getBotSummary(_state) {
    return _state.botSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
  getContactsReport(_state) {
    return _state.contactsReport.data;
  },
  getContactsReportIsFetching(_state) {
    return _state.contactsReport.isFetching;
  },
  getContactsSummary(_state) {
    return _state.contactsSummary;
  },
};

const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric,
        value: false,
      });
    });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(
        data,
        state.overview.accountConversationHeatmap
      );

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getSummary(
      reportObj.from,
      reportObj.to,
      reportObj.type,
      reportObj.id,
      reportObj.groupBy,
      reportObj.businessHours
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchBotSummary({ commit }, reportObj) {
    Report.getBotSummary({
      from: reportObj.from,
      to: reportObj.to,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
    })
      .then(botSummary => {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type, reportObj.page)
      .then(agentConversationMetric => {
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC,
          agentConversationMetric.data
        );
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  async downloadAgentReports(_, reportObj) {
    const response = await Report.getAgentReports(reportObj);
    downloadCsvFile(reportObj.fileName, response.data);
    AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
      reportType: 'agent',
      businessHours: reportObj?.businessHours,
    });
  },
  async downloadLabelReports(_, reportObj) {
    const response = await Report.getLabelReports(reportObj);
    downloadCsvFile(reportObj.fileName, response.data);
    AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
      reportType: 'label',
      businessHours: reportObj?.businessHours,
    });
  },
  async downloadInboxReports(_, reportObj) {
    const response = await Report.getInboxReports(reportObj);
    downloadCsvFile(reportObj.fileName, response.data);
    AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
      reportType: 'inbox',
      businessHours: reportObj?.businessHours,
    });
  },
  async downloadTeamReports(_, reportObj) {
    const response = await Report.getTeamReports(reportObj);
    downloadCsvFile(reportObj.fileName, response.data);
    AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
      reportType: 'team',
      businessHours: reportObj?.businessHours,
    });
  },
  async downloadAccountConversationHeatmap(_, reportObj) {
    const response = await Report.getConversationTrafficCSV();
    downloadCsvFile(
      generateFileName({
        type: 'Conversation traffic',
        to: reportObj.to,
      }),
      response.data
    );
    AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
      reportType: 'conversation_heatmap',
      businessHours: false,
    });
  },
  fetchContactsReport({ commit }, reportObj) {
    commit(types.default.TOGGLE_CONTACTS_REPORT_LOADING, true);
    Report.getContactsReport(reportObj)
      .then(response => {
        const { data } = response.data;
        const clampedData = clampDataBetweenTimeline(
          data,
          reportObj.from,
          reportObj.to
        );
        commit(types.default.SET_CONTACTS_REPORT, clampedData);
        commit(types.default.TOGGLE_CONTACTS_REPORT_LOADING, false);
      })
      .catch(error => {
        console.error(error);
        commit(types.default.TOGGLE_CONTACTS_REPORT_LOADING, false);
      });
  },
  fetchContactsSummary({ commit }, reportObj) {
    Report.getContactsSummary(reportObj)
      .then(response => {
        commit(types.default.SET_CONTACTS_SUMMARY, response.data);
      })
      .catch(error => {
        console.error(error);
      });
  },
  async downloadContactsReport(_, reportObj) {
    const response = await Report.getContactsCSV(reportObj);
    downloadCsvFile(
      generateFileName({
        type: 'Contacts',
        to: reportObj.to,
      }),
      response.data
    );
    AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
      reportType: 'contacts',
    });
  },
  downloadCombinedReport(_, reportObj) {
    return Report.getCombinedReports(reportObj).then(response => {
      downloadCsvFile(reportObj.fileName, response.data);
      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'combined',
        primary: reportObj.primary,
        secondary: reportObj.secondary,
        groupBy: reportObj.groupBy,
        businessHours: reportObj?.businessHours,
      });
    });
  },
  downloadPipelineReport(_, reportObj) {
    return Report.getPipelineReportCSV(reportObj).then(response => {
      downloadCsvFile(reportObj.fileName, response.data);
      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'pipeline',
        pipelineId: reportObj.pipelineId,
        stageId: reportObj.stageId,
        label: reportObj.label,
      });
    });
  },
};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
  [types.default.SET_CONTACTS_REPORT](_state, data) {
    _state.contactsReport.data = data;
  },
  [types.default.TOGGLE_CONTACTS_REPORT_LOADING](_state, flag) {
    _state.contactsReport.isFetching = flag;
  },
  [types.default.SET_CONTACTS_SUMMARY](_state, summaryData) {
    _state.contactsSummary = summaryData;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
